import {InvalidTokenError} from '@packages/utils';
import {parseCookies} from 'nookies';
import {useCallback, useState} from 'react';
import {SELECTION, UserMeClipType} from '../../../core/entities/clip/constants';
import {convertToLowerCase} from '../../../core/factories/clips/convertToLowerCase';
import {
  type ReportsRepositoryProps,
  deleteReportsMyClips,
  updateReportsMyClips,
} from '../../../core/repositories/clipsRepository';
import {useIsSessionExpired} from '../../../core/usecases/auth/useIsSessionExpired';

type HandleClipUpdateProps = Omit<ReportsRepositoryProps, 'cookies'> & {
  clipCount: number;
  clippedAt: string;
};

export function useHandleReportClip({
  reportId,
  reportType,
  clipCount,
  clippedAt,
}: HandleClipUpdateProps) {
  const [clipCountUpdated, setClipCountUpdated] = useState<number>(clipCount);
  const [isClipped, setIsClipped] = useState<boolean>(Boolean(clippedAt));
  const {updateIsSessionExpired} = useIsSessionExpired();

  const deleteReportUseCase = useCallback(
    async function deleteUseCase(reportTypeConvertToLowerCase: string) {
      const cookies = parseCookies();
      const result = await deleteReportsMyClips({
        cookies,
        reportId,
        reportType: reportTypeConvertToLowerCase,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) {
        return;
      }

      setIsClipped(false);
      setClipCountUpdated(result.value.clipsCount);
    },
    [reportId],
  );

  const updateReportUseCase = useCallback(
    async function updateUseCase(reportTypeConvertToLowerCase: string) {
      const cookies = parseCookies();
      const result = await updateReportsMyClips({
        cookies,
        reportId,
        reportType: reportTypeConvertToLowerCase,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) {
        return;
      }

      setIsClipped(true);
      setClipCountUpdated(result.value.clipsCount);
    },
    [reportId],
  );

  const handleClip = useCallback(async () => {
    // MEMO: ReportTypeを小文字に変換する
    const reportTypeConvertToLowerCase = convertToLowerCase(
      reportType === String(UserMeClipType.ENTRY_SHEET)
        ? reportType
        : SELECTION,
    );
    // MEMO: ここでクリップの状態を判定して、クリップ済みの場合はクリップを解除する
    // 各ユースケースを呼び出す
    if (isClipped) {
      await deleteReportUseCase(reportTypeConvertToLowerCase);
      return;
    }

    await updateReportUseCase(reportTypeConvertToLowerCase);
  }, [deleteReportUseCase, isClipped, reportType, updateReportUseCase]);

  return {handleClip, clipCountUpdated, isClipped};
}
